/**
 * Trade between players: what the server pushes on the live socket
 * (apps/server/src/trade.ts) — a request, the open session, its end.
 */

export interface TradeItemView {
  uid: number;
  container: 'backpack' | 'satchel';
  index: number;
  itemId: number;
  name: string;
  count: number;
  tier?: number;
  charges?: number;
  imbued?: boolean;
  /** Running imbuements of this copy, by shrine slot (the partner's offer paints its pips with them). */
  imbuements?: Array<{ index: number; type: string }>;
}

export interface TradeSideView {
  items: TradeItemView[];
  gold: number;
  ready: boolean;
  confirmed: boolean;
}

export interface TradeSessionView {
  type: 'trade-session';
  partnerId: number;
  partnerName: string;
  mine: TradeSideView;
  theirs: TradeSideView;
  note?: string;
}

export type TradeCloseReason = 'complete' | 'cancelled' | 'declined' | 'gone' | 'far';

export type TradeEvent =
  | { type: 'trade-requested'; fromId: number; fromName: string }
  | TradeSessionView
  | { type: 'trade-closed'; reason: TradeCloseReason };

const listeners = new Set<(event: TradeEvent) => void>();

export function isTradeEvent(message: { type?: unknown }): message is TradeEvent {
  return message.type === 'trade-requested' || message.type === 'trade-session' || message.type === 'trade-closed';
}

export function pushTradeEvent(event: TradeEvent): void {
  for (const listener of listeners) listener(event);
}

export function subscribeTrade(listener: (event: TradeEvent) => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Reference system lines ("O trade foi concluído." …). */
export const TRADE_CLOSED_TEXT: Record<TradeCloseReason, string> = {
  complete: 'O trade foi concluído.',
  cancelled: 'O trade foi cancelado.',
  declined: 'O trade foi recusado.',
  gone: 'O trade acabou — o outro jogador saiu.',
  far: 'O trade acabou — vocês se afastaram demais.',
};
